import { useState, useCallback } from "react";
import {
  Sparkles,
  Zap,
  Play,
  FileSpreadsheet,
  FileText,
  AlertCircle,
  Pencil,
  Check,
  X,
  HelpCircle,
  MessageSquare,
  History,
  Clock,
  MessageSquareCode,
} from "lucide-react";
import { PageHeader } from "@/components/molecules/PageHeader";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { Button } from "@/components/atoms/studio/ui/button";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import {
  useGenerateSoql,
  useExecuteSoql,
  useRequestSoqlCorrection,
} from "@/hooks/text-to-soql";
import type { QueryResult, ClarificationData, QueryHistoryEntry } from "@/services/text-to-soql";
import { formatColumnLabel, normalizeExecuteResult } from "./normalizeResults";

const MAX_SELF_CORRECT_ATTEMPTS = 2;

const EXAMPLE_QUERIES = [
  "Get all accounts created this year with annual revenue over 1 million",
  "Find contacts where email contains gmail.com and they are active",
  "How many opportunities were created today",
] as const;

const EXPORT_SLUG_MAX = 48;

/**
 * Query-specific export filename: the user's question (slugified) + a
 * minute-resolution timestamp, e.g. `Top_5_Opportunities_By_Amount_20260812_0915.csv`.
 * Falls back to the SOQL's FROM object when no question is available (hand-edited
 * query). Fixes the `soql-results (3).csv` pile-up — every download names its query.
 */
function exportFilename(question: string, soql: string, ext: "csv" | "xlsx"): string {
  const fromObject = /\bFROM\s+([A-Za-z0-9_]+)/i.exec(soql)?.[1];
  const base =
    question.replace(/\(clarification:.*?\)/gi, " ").trim() ||
    (fromObject ? `${fromObject} results` : "soql results");
  const slug =
    base
      .replace(/[^A-Za-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, EXPORT_SLUG_MAX)
      .replace(/_+$/g, "") || "soql_results";
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp =
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
    `_${pad(d.getHours())}${pad(d.getMinutes())}`;
  return `${slug}_${stamp}.${ext}`;
}

export const TextToSOQL = () => {
  const [query, setQuery] = useState("");
  const [generatedSOQL, setGeneratedSOQL] = useState("");
  const [results, setResults] = useState<QueryResult | null>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [genExplanation, setGenExplanation] = useState<string | null>(null);
  const [correctionLog, setCorrectionLog] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedSOQL, setEditedSOQL] = useState("");

  // Clarification state
  const [needsClarification, setNeedsClarification] = useState(false);
  const [clarificationData, setClarificationData] = useState<ClarificationData | null>(null);
  const [previousMessage, setPreviousMessage] = useState("");

  // Query attempt history (only entries that produced a SOQL query)
  const [queryHistory, setQueryHistory] = useState<QueryHistoryEntry[]>([]);

  // Right column panel toggle
  const [showHistory, setShowHistory] = useState(false);

  const selectedOrg = useAuthStoreV1((s) => s.selectedOrg);
  const { generateSoql, isPending: isGenerating } = useGenerateSoql();
  const { executeSoql } = useExecuteSoql();
  const { requestCorrection } = useRequestSoqlCorrection();

  const handleStartEdit = useCallback(() => {
    setEditedSOQL(generatedSOQL);
    setIsEditing(true);
  }, [generatedSOQL]);

  const handleApplyEdit = useCallback(() => {
    setGeneratedSOQL(editedSOQL);
    setIsEditing(false);
  }, [editedSOQL]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditedSOQL("");
  }, []);

  const handleGenerate = async () => {
    if (!query.trim()) return;

    // A clarification answer must ride WITH the original question — sending the
    // bare answer ("Account") makes the engine plan the answer as a brand-new
    // question (bug found 2026-08-11: "ancestors of QA_X" + "Account" returned
    // SELECT * FROM Account). previousMessage was stored but never used.
    const answerText = query.trim();
    const currentMessage =
      needsClarification && previousMessage
        ? `${previousMessage} (clarification: ${answerText})`
        : answerText;
    setPreviousMessage(currentMessage);
    setError(null);
    setGeneratedSOQL("");
    setGenExplanation(null);
    setResults(null);
    setShowHistory(false);

    try {
      const data = await generateSoql(currentMessage);

      if (data?.error) {
        setError(data.error);
        setNeedsClarification(false);
        setClarificationData(null);
        return;
      }

      const hasClarification = data?.needs_clarification === true;
      setNeedsClarification(hasClarification);
      setClarificationData(hasClarification ? (data?.clarification ?? null) : null);

      if (data?.query) {
        setGeneratedSOQL(data.query);
        setGenExplanation(data?.explanation || null);
        // Add to history regardless of clarification state
        setQueryHistory((prev) => [
          ...prev,
          { id: Date.now(), message: currentMessage, soql: data.query! },
        ]);
      }

      // Clear textarea so user can type clarification
      if (hasClarification) {
        setQuery("");
      }

      // If no query and no clarification and no error — unexpected
      if (!data?.query && !hasClarification && !data?.error) {
        setError("No SOQL query was generated. Try rephrasing your question.");
      }
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Failed to connect to graph service",
      );
      setNeedsClarification(false);
      setClarificationData(null);
    }
  };

  const runExecute = async (soqlToRun: string) => {
    if (!soqlToRun || !selectedOrg) return;
    setIsExecuting(true);
    setError(null);
    setCorrectionLog([]);
    let currentQuery = soqlToRun;
    let attempts = 0;
    const logs: string[] = [];

    try {
      while (attempts <= MAX_SELF_CORRECT_ATTEMPTS) {
        const data = await executeSoql(currentQuery);

        if (data && "success" in data && data.success && "records" in data) {
          setResults(normalizeExecuteResult(data, currentQuery));
          setShowHistory(false);
          if (currentQuery !== generatedSOQL) setGeneratedSOQL(currentQuery);
          setCorrectionLog(logs);
          return;
        }

        const sfError =
          data && "error" in data ? (data as { error: string }).error : "Unknown error";
        attempts++;

        if (attempts > MAX_SELF_CORRECT_ATTEMPTS) {
          setError(
            `Query failed after ${MAX_SELF_CORRECT_ATTEMPTS} correction attempts: ${sfError}`,
          );
          setCorrectionLog(logs);
          return;
        }

        logs.push(`Attempt ${attempts}: "${sfError}" — asking agent to fix...`);
        setCorrectionLog([...logs]);

        const correction = await requestCorrection({
          originalMessage: previousMessage || soqlToRun,
          failedQuery: currentQuery,
          sfError,
        });
        if (!correction) {
          setError(`Query failed and agent could not correct it: ${sfError}`);
          setCorrectionLog(logs);
          return;
        }

        logs.push(`Attempt ${attempts}: Agent corrected query`);
        setCorrectionLog([...logs]);
        setGeneratedSOQL(correction.query);
        currentQuery = correction.query;
      }
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Failed to execute query",
      );
    } finally {
      setIsExecuting(false);
    }
  };

  const handleExecute = () => runExecute(generatedSOQL);

  const handleExecuteFromHistory = useCallback(
    (entry: QueryHistoryEntry) => {
      setGeneratedSOQL(entry.soql);
      setResults(null);
      setShowHistory(false);
      // Run after state update via a small trick — call runExecute directly
      runExecute(entry.soql);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedOrg, previousMessage],
  );

  const handleDownload = (format: "csv" | "excel") => {
    if (!results) return;
    const header = results.columns
      .map((c) => `"${formatColumnLabel(c)}"`)
      .join(",");
    const rows = results.rows.map((r) =>
      results.columns
        .map((c) => `"${(r[c] ?? "").replace(/"/g, '""')}"`)
        .join(","),
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = exportFilename(
      previousMessage,
      generatedSOQL,
      format === "csv" ? "csv" : "xlsx",
    );
    a.click();
    URL.revokeObjectURL(url);
  };

  const showRightHistory = !results || showHistory;

  return (
    <div className="flex h-full flex-col gap-4 animate-fade-in">
      <PageHeader icon={MessageSquareCode} title="Text to SOQL" description="Ask in plain English, generate and execute Salesforce SOQL, and download results" />

      <div className="flex-1 min-h-0 grid gap-5 lg:grid-cols-[24rem_minmax(0,1fr)] xl:grid-cols-[28rem_minmax(0,1fr)]">
      {/* Left Column */}
      <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">

        {/* Main Input Area */}
        <div className="studio-card p-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-primary">
              <Zap className="h-4 w-4" />
              <span className="text-xs font-semibold">Describe Your Query</span>
            </div>

            {/* Previous message chip — shown in Case A (clarification, no query) */}
            {needsClarification && !generatedSOQL && previousMessage && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2">
                <MessageSquare className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-[10px] font-medium text-amber-600/70 uppercase tracking-wide mb-0.5">
                    You asked
                  </p>
                  <p className="text-xs text-amber-700 line-clamp-2">{previousMessage}</p>
                </div>
              </div>
            )}

            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                needsClarification
                  ? "Add more details or clarify your query..."
                  : "Describe your query in plain English..."
              }
              className="min-h-[96px] w-full resize-none rounded-md border border-input bg-card px-3 py-2 text-sm placeholder:text-muted-foreground outline-none transition-all focus-visible:ring-2 focus-visible:ring-ring"
              rows={3}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleGenerate();
                }
              }}
            />

            {/* Example queries — hidden when clarification is active */}
            {!needsClarification && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">
                  Examples:
                </p>
                <div className="flex flex-col gap-1.5">
                  {EXAMPLE_QUERIES.map((example, index) => (
                    <button
                      key={index}
                      onClick={() => setQuery(example)}
                      className="group text-left rounded-md border border-border bg-card px-2 py-1.5 text-xs transition-all hover:border-primary/50 hover:bg-muted"
                    >
                      <span className="text-muted-foreground group-hover:text-foreground transition-colors line-clamp-2">
                        {example}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Clarification banner — shown in both Case A and Case B */}
            {needsClarification && clarificationData && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/8 p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <HelpCircle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                  <p className="text-xs font-semibold text-amber-700">
                    Help us understand better
                  </p>
                </div>
                <ul className="space-y-1 pl-1">
                  {clarificationData.questions.map((q, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-amber-700/90">
                      <span className="mt-1.5 h-1 w-1 rounded-full bg-amber-500 shrink-0" />
                      {q}
                    </li>
                  ))}
                </ul>
                {generatedSOQL && (
                  <p className="text-[10px] text-amber-600/70 pt-1 border-t border-amber-500/20">
                    A best-effort query was generated below — feel free to test it.
                  </p>
                )}
              </div>
            )}

            <Button
              onClick={handleGenerate}
              disabled={!query.trim() || isGenerating}
              size="sm"
              className="w-full gap-2"
            >
              {isGenerating ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  {needsClarification ? "Clarify & Generate" : "Generate SOQL"}
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Correction Log */}
        {correctionLog.length > 0 && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 space-y-1">
            <p className="text-xs font-semibold text-amber-600">
              Self-correcting...
            </p>
            {correctionLog.map((log, i) => (
              <p key={i} className="text-xs text-amber-600/80">
                {log}
              </p>
            ))}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
            <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
            <p className="text-xs text-destructive">{error}</p>
          </div>
        )}

        {/* Generated Query Card — shown in Case B (clarification + query) and Case C (success) */}
        {generatedSOQL && (
          <div className="studio-card p-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Zap className="h-4 w-4 text-primary" />
                  <span className="text-xs font-semibold">Generated Query</span>
                  {needsClarification && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700 border-amber-300">
                      Best effort
                    </Badge>
                  )}
                </div>
                {!isEditing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleStartEdit}
                    className="h-6 px-2 gap-1 text-xs"
                  >
                    <Pencil className="h-3 w-3" />
                    Edit
                  </Button>
                )}
              </div>

              {isEditing ? (
                <>
                  <textarea
                    value={editedSOQL}
                    onChange={(e) => setEditedSOQL(e.target.value)}
                    className="w-full bg-muted/50 rounded-md p-3 border border-primary/30 text-xs text-foreground font-mono resize-y min-h-[80px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                    rows={4}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleApplyEdit}
                      className="flex-1 gap-1"
                    >
                      <Check className="h-3 w-3" />
                      Apply Changes
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdit}
                      className="gap-1"
                    >
                      <X className="h-3 w-3" />
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-muted/50 rounded-md p-3 border border-border/50">
                    <code className="text-xs text-foreground font-mono block overflow-x-auto whitespace-pre-wrap">
                      {generatedSOQL}
                    </code>
                  </div>
                  {/* Engine explanation + interpretation notes (which date window was used,
                      how an absolute period was bounded) — previously discarded, which made
                      deliberate interpretations look like bugs to testers. */}
                  {genExplanation && (
                    <p className="text-[11px] text-muted-foreground leading-snug">
                      {genExplanation}
                    </p>
                  )}
                  <Button
                    onClick={handleExecute}
                    disabled={isExecuting || !selectedOrg}
                    size="sm"
                    className="w-full gap-2"
                  >
                    {isExecuting ? (
                      <>
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        Executing...
                      </>
                    ) : (
                      <>
                        <Play className="h-3 w-3" />
                        Execute Query
                      </>
                    )}
                  </Button>
                  {!selectedOrg && (
                    <p className="text-xs text-muted-foreground text-center">
                      Connect a Salesforce org to execute queries
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Right Column — Results or Query History */}
      <div className="min-w-0">
        {!showRightHistory ? (
          /* Results Panel */
          <div className="term-window flex h-full min-h-0 flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">Query Results</span>
                <Badge variant="secondary" className="text-xs">
                  {results!.totalCount} records
                </Badge>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {queryHistory.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowHistory(true)}
                    className="h-7 px-2 gap-1.5 text-xs text-muted-foreground"
                  >
                    <History className="h-3 w-3" />
                    History
                    <Badge variant="secondary" className="text-[10px] px-1 py-0 ml-0.5">
                      {queryHistory.length}
                    </Badge>
                  </Button>
                )}
                <Button
                  onClick={() => handleDownload("csv")}
                  disabled={results!.rows.length === 0}
                  size="sm"
                  variant="outline"
                  className="gap-2"
                >
                  <FileText className="h-3 w-3" />
                  CSV
                </Button>
                <Button
                  onClick={() => handleDownload("excel")}
                  disabled={results!.rows.length === 0}
                  size="sm"
                  variant="outline"
                  className="gap-2"
                >
                  <FileSpreadsheet className="h-3 w-3" />
                  Excel
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-auto">
              {results!.rows.length === 0 ? (
                <div className="h-full flex items-center justify-center p-4">
                  <div className="text-center text-muted-foreground">
                    <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm font-medium">No records returned</p>
                    <p className="text-xs mt-1">
                      The query ran successfully but matched no records
                    </p>
                  </div>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm border-b border-border">
                    <tr>
                      {results!.columns.map((col) => (
                        <th
                          key={col}
                          className="px-4 py-3 text-left font-semibold text-muted-foreground whitespace-nowrap"
                        >
                          {formatColumnLabel(col)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results!.rows.map((row, i) => (
                      <tr
                        key={i}
                        className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                      >
                        {results!.columns.map((col) => (
                          <td
                            key={col}
                            className="px-4 py-3 text-foreground whitespace-nowrap"
                          >
                            {row[col]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        ) : (
          /* Query History Panel */
          <div className="term-window flex h-full flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border shrink-0">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-semibold">Query History</span>
                {queryHistory.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {queryHistory.length} {queryHistory.length === 1 ? "attempt" : "attempts"}
                  </Badge>
                )}
              </div>
              {results && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowHistory(false)}
                  className="h-7 px-2 gap-1.5 text-xs text-muted-foreground"
                >
                  <Play className="h-3 w-3" />
                  Back to Results
                </Button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {queryHistory.length === 0 ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center text-muted-foreground">
                    <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm font-medium">No results yet</p>
                    <p className="text-xs mt-1">
                      Generate and execute a query to see results
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {queryHistory.map((entry, index) => (
                    <div
                      key={entry.id}
                      className="rounded-lg border border-border bg-background/50 p-3 space-y-2"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-2 min-w-0">
                          <Badge
                            variant="secondary"
                            className="text-[10px] px-1.5 py-0 shrink-0 mt-0.5"
                          >
                            #{index + 1}
                          </Badge>
                          <p className="text-xs text-muted-foreground line-clamp-2 min-w-0">
                            {entry.message}
                          </p>
                        </div>
                      </div>
                      <div className="bg-muted/50 rounded-md p-2.5 border border-border/50">
                        <code className="text-[11px] text-foreground font-mono block overflow-x-auto whitespace-pre-wrap line-clamp-3">
                          {entry.soql}
                        </code>
                      </div>
                      <div className="flex justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleExecuteFromHistory(entry)}
                          disabled={isExecuting || !selectedOrg}
                          className="h-7 px-3 gap-1.5 text-xs"
                        >
                          <Play className="h-3 w-3" />
                          Execute
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};
